import { useEffect, useState } from "react";

import AttachedCloudIntegrationsSection from "components/AttachCloud/Details/CloudIntegrationsSection";
import AttachCloudForm from "components/AttachCloud/Form";
import { AttachCloudEntityType } from "components/AttachCloud/Form/types";
import DocumentationButton from "components/DocumentationButton";
import FormLoading from "components/form/components/loading";
import { EmptystateCloudColored } from "components/icons/generated";
import Banner from "ds/components/Banner";
import Box from "ds/components/Box";
import EmptyState from "ds/components/EmptyState";
import FullScreenModalBody from "ds/components/FullScreenModal/Body";
import FullScreenModalBodyHeader from "ds/components/FullScreenModal/BodyHeader";
import Typography from "ds/components/Typography";
import useTypedContext from "hooks/useTypedContext";
import { CLOUD_INTEGRATIONS } from "shared/CloudIntegration/types";
import useAttachableCloudIntegrations from "shared/CloudIntegration/useAttachableCloudIntegrations";
import { getDocsUrl } from "utils/getDocsUrl";

import { StackFormContext } from "../context";
import NewStackFooter from "../Footer";
import { StackCreationWizardStep } from "../types";
import useErrorHandlerNewStack from "../useErrorHandlerNewStack";
import NewStackAttachCloudDocumentation from "./Documentation";
import useAttachedCloudIntegration from "./hooks/useAttachedCloudIntegration";

const refetchQueries = ["GetAttachedStackIntegrations"];

const NewStackAttachCloud = () => {
  const { formData, createdStackId } = useTypedContext(StackFormContext);
  const spaceId = formData[StackCreationWizardStep.Details].space;
  const stackLabels = formData[StackCreationWizardStep.Details].labels;

  const {
    loading: integrationLoading,
    autoAttachedIntegrations,
    manuallyAttachedIntegrations,
    refetch: refetchAttachedCloudIntegrations,
  } = useAttachedCloudIntegration();

  const onSuccessDetach = () => {
    refetchAttachedCloudIntegrations();
  };

  const {
    loading: cloudConfigLoading,
    cloudConfig,
    attachableAzureIntegrations,
    attachableAwsIntegrations,
    hasData,
    refetch,
    refetching,
    error,
  } = useAttachableCloudIntegrations(spaceId);

  const [cloud, setCloud] = useState<CLOUD_INTEGRATIONS>(cloudConfig[0].type);

  useEffect(() => {
    setCloud(cloudConfig[0].type);
  }, [cloudConfig]);

  useErrorHandlerNewStack(error);

  if (cloudConfigLoading || integrationLoading) {
    return <FormLoading />;
  }

  const hasNoClouds =
    hasData &&
    !attachableAwsIntegrations.length &&
    !attachableAzureIntegrations.length &&
    (!manuallyAttachedIntegrations.length || !autoAttachedIntegrations.length);

  return (
    <>
      <FullScreenModalBody>
        <FullScreenModalBodyHeader title="Attach cloud integration (optional)" />

        {hasNoClouds ? (
          <Box direction="column" gap="large">
            <Banner variant="info">
              You can only attach integrations from the current space and parent spaces that you
              inherit from.
            </Banner>
            <EmptyState
              padding="large"
              icon={EmptystateCloudColored}
              title="You do not have any cloud integrations yet"
              caption={
                <>
                  Cloud Integrations are used to dynamically generate short-lived credentials to
                  authenticate to cloud providers (AWS/Azure/GCP currently supported). In this way
                  you avoid using static credentials and by doing so, you reduce breaches.
                  <br /> Read more in the Documentation.
                </>
              }
            >
              <DocumentationButton
                to={getDocsUrl("/integrations/cloud-providers")}
                label="Documentation"
              />
            </EmptyState>
          </Box>
        ) : (
          <>
            <Typography
              tag="p"
              variant="p-body2"
              margin="small 0 large 0"
              align="center"
              color="secondary"
            >
              Integrate stack with your cloud provider
            </Typography>

            {manuallyAttachedIntegrations.length ? (
              <AttachedCloudIntegrationsSection
                title="Attached directly"
                integrations={manuallyAttachedIntegrations}
                cloudConfig={cloudConfig}
                stackId={createdStackId}
                readOnly={false}
                onSuccessDetach={onSuccessDetach}
                stackLabels={stackLabels.map((label) => label.value)}
              />
            ) : (
              <Box padding="0 x-large" direction="column">
                <AttachCloudForm
                  entityId={createdStackId}
                  entityType={AttachCloudEntityType.Stack}
                  hasData={hasData}
                  refetch={refetch}
                  refetching={refetching}
                  setCloud={setCloud}
                  cloud={cloud}
                  cloudConfig={cloudConfig}
                  attachableAzureIntegrations={attachableAzureIntegrations}
                  attachableAwsIntegrations={attachableAwsIntegrations}
                  refetchQueries={refetchQueries}
                />
              </Box>
            )}
            {autoAttachedIntegrations.length ? (
              <AttachedCloudIntegrationsSection
                title="Auto-attached"
                integrations={autoAttachedIntegrations}
                cloudConfig={cloudConfig}
                stackId={createdStackId}
                readOnly={true}
                isAutoAttached={true}
                stackLabels={stackLabels.map((label) => label.value)}
              />
            ) : null}
          </>
        )}
      </FullScreenModalBody>

      <NewStackAttachCloudDocumentation cloud={cloud}>
        {({ body, link }) => (
          <NewStackFooter
            documentationLink={link}
            documentationTitle="Cloud integration"
            documentationBody={body}
          />
        )}
      </NewStackAttachCloudDocumentation>
    </>
  );
};

export default NewStackAttachCloud;
